import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useOperator } from '../../contexts/OperatorContext';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import ReactECharts from 'echarts-for-react';
import * as FiIcons from 'react-icons/fi';
import AvailableOrders from '../../pages/operator/AvailableOrders';
import ScreenShareSession from '../../components/operator/ScreenShareSession';
import supabase from '../../lib/supabase';
import formatOrderId from '../../utils/formatOrderId';

const { 
  FiFileText, FiClock, FiCheckCircle, FiTrendingUp, 
  FiDollarSign, FiUser, FiCalendar, FiBell, FiStar,
  FiPhone, FiVideo, FiList, FiCheckSquare
} = FiIcons;

const OperatorDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalApplications: 0,
    pendingApplications: 0,
    completedApplications: 0,
    earnings: 0
  });
  const { availableOrders, assignedOrders, transactions, walletBalance, fetchAvailableOrders, fetchAssignedOrders, fetchTransactions, operatorProfile } = useOperator();
  const [recentAvailable, setRecentAvailable] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [showScreenShare, setShowScreenShare] = useState(false);
  const [performanceStats, setPerformanceStats] = useState({
    successRate: 0,
    avgCompletionTime: 0,
    customerRating: 0
  });
  const [todaysSchedule, setTodaysSchedule] = useState({
    dueToday: 0,
    pendingPayment: 0
  });

  useEffect(() => {
    const fetchStats = async () => {
      // Kick off operator data fetches
      fetchAvailableOrders && fetchAvailableOrders();
      fetchAssignedOrders && fetchAssignedOrders();
      fetchTransactions && fetchTransactions();
      
      // Fetch realtime notifications
      await fetchNotifications();
      
      // Fetch realtime performance stats
      await fetchPerformanceStats();
      
      // Fetch today's schedule
      await fetchTodaysSchedule();
    };

    fetchStats();
    
    // Set up realtime subscriptions
    const setupRealtimeSubscriptions = () => {
      // Subscribe to operator_orders changes for realtime updates
      const ordersChannel = supabase
        .channel('operator-orders-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'operator_orders_a7b5c9',
            filter: `operator_id=eq.${operatorProfile?.id}`
          },
          (payload) => {
            console.log('Realtime order update:', payload);
            // Refresh data when changes occur
            fetchAssignedOrders && fetchAssignedOrders();
            fetchPerformanceStats();
            fetchTodaysSchedule();
            
            // Add notification for new assignment
            if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
              fetchNotifications();
            }
          }
        )
        .subscribe();
      
      // Subscribe to transactions for payment notifications
      const transactionsChannel = supabase
        .channel('operator-transactions-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'operator_transactions_a7b5c9',
            filter: `operator_id=eq.${operatorProfile?.id}`
          },
          (payload) => {
            console.log('Realtime transaction update:', payload);
            fetchTransactions && fetchTransactions();
            fetchNotifications();
          }
        )
        .subscribe();
      
      return () => {
        supabase.removeChannel(ordersChannel);
        supabase.removeChannel(transactionsChannel);
      };
    };
    
    const cleanup = setupRealtimeSubscriptions();
    return cleanup;
  }, [fetchAvailableOrders, fetchAssignedOrders, fetchTransactions, user?.id, operatorProfile?.id]);

  // Recompute dashboard stats when operator data changes
  useEffect(() => {
    // Total applications = assigned orders for this operator
    const totalApplications = Array.isArray(assignedOrders) ? assignedOrders.length : 0;

    // Pending = assigned + in_progress
    const pendingApplications = Array.isArray(assignedOrders)
      ? assignedOrders.filter(o => ['assigned', 'in_progress'].includes((o.status || '').toLowerCase())).length
      : 0;

    // Completed = completed or pdf_submitted
    const completedApplications = Array.isArray(assignedOrders)
      ? assignedOrders.filter(o => ['completed', 'pdf_submitted'].includes((o.status || '').toLowerCase())).length
      : 0;

    // Earnings = sum of completed credit transactions
    const earnings = Array.isArray(transactions)
      ? transactions
          .filter(t => (t.type || '').toLowerCase() === 'credit' && (t.status || '').toLowerCase() === 'completed')
          .reduce((sum, t) => sum + (Number(t.amount) || 0), 0)
      : 0;

    setStats({ totalApplications, pendingApplications, completedApplications, earnings });
  }, [assignedOrders, transactions]);

  // Derive recent available applications from context
  useEffect(() => {
    if (Array.isArray(availableOrders)) {
      const sorted = [...availableOrders].sort((a, b) => {
        const ad = new Date(a.created_at || 0).getTime();
        const bd = new Date(b.created_at || 0).getTime();
        return bd - ad;
      });
      setRecentAvailable(sorted.slice(0, 5));
    } else {
      setRecentAvailable([]);
    }
  }, [availableOrders]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'in_progress': return 'text-blue-600 bg-blue-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return FiCheckCircle;
      case 'in_progress': return FiClock;
      default: return FiClock;
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'new_assignment': return FiFileText;
      case 'payment': return FiDollarSign;
      default: return FiBell;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'new_assignment': return 'text-blue-600 bg-blue-100';
      case 'payment': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  // Chart options for applications trend - Dynamic from assignedOrders (last 7 days)
  const applicationChartOptions = React.useMemo(() => {
    // Build last 7 day labels (Mon..Sun localized) and counts
    const today = new Date();
    const days = [...Array(7)].map((_, i) => {
      const d = new Date(today);
      d.setDate(today.getDate() - (6 - i));
      return d;
    });
    const labels = days.map(d => d.toLocaleDateString('en-IN', { weekday: 'short' }));

    const countsByDay = days.map(d => {
      const y = d.getFullYear();
      const m = d.getMonth();
      const day = d.getDate();
      const start = new Date(y, m, day, 0, 0, 0, 0).getTime();
      const end = new Date(y, m, day, 23, 59, 59, 999).getTime();
      return (Array.isArray(assignedOrders) ? assignedOrders : []).filter(o => {
        const t = new Date(o.assigned_at || o.processing_started_at || o.created_at || 0).getTime();
        return t >= start && t <= end;
      }).length;
    });

    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
      xAxis: { type: 'category', data: labels },
      yAxis: { type: 'value' },
      series: [
        { name: 'Applications', type: 'bar', data: countsByDay, itemStyle: { color: '#0ea5e9' } }
      ]
    };
  }, [assignedOrders]);

  // Chart options for earnings - Dynamic from transactions (last 6 months)
  const earningsChartOptions = React.useMemo(() => {
    const now = new Date();
    // Build last 6 months labels oldest->newest
    const months = [...Array(6)].map((_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
      return d;
    });
    const labels = months.map(d => d.toLocaleString('en-IN', { month: 'short' }));

    const sums = months.map((d, idx) => {
      const y = d.getFullYear();
      const m = d.getMonth();
      const start = new Date(y, m, 1, 0, 0, 0, 0).getTime();
      const end = new Date(y, m + 1, 0, 23, 59, 59, 999).getTime();
      const tx = Array.isArray(transactions) ? transactions : [];
      const filtered = tx.filter(t => (t.type || '').toLowerCase() === 'credit' && (t.status || '').toLowerCase() === 'completed');
      const sum = filtered.reduce((acc, t) => {
        const ts = new Date(t.transaction_date || t.created_at || 0).getTime();
        if (ts >= start && ts <= end) return acc + (Number(t.amount) || 0);
        return acc;
      }, 0);
      return sum;
    });

    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'line' } },
      grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
      xAxis: { type: 'category', data: labels },
      yAxis: { type: 'value' },
      series: [
        {
          name: 'Earnings',
          type: 'line',
          smooth: true,
          data: sums,
          itemStyle: { color: '#22c55e' },
          areaStyle: {
            color: {
              type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(34, 197, 94, 0.3)' },
                { offset: 1, color: 'rgba(34, 197, 94, 0.05)' }
              ]
            }
          }
        }
      ]
    };
  }, [transactions]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Fetch realtime notifications from database
  const fetchNotifications = async () => {
    try {
      if (!operatorProfile?.id) return;
      
      const operatorId = operatorProfile?.id;
      
      // Fetch recent assigned orders for notifications
      const { data: recentOrders, error: ordersError } = await supabase
        .from('operator_orders_a7b5c9')
        .select('*')
        .eq('operator_id', operatorId)
        .order('assigned_at', { ascending: false })
        .limit(5);
      
      if (ordersError) {
        console.error('Error fetching notifications:', ordersError);
        return;
      }
      
      // Fetch recent transactions for payment notifications
      const { data: recentTransactions, error: txError } = await supabase
        .from('operator_transactions_a7b5c9')
        .select('*')
        .eq('operator_id', operatorId)
        .eq('type', 'credit')
        .order('transaction_date', { ascending: false })
        .limit(3);
      
      if (txError) {
        console.error('Error fetching transactions:', txError);
      }
      
      // Build notifications array
      const notifs = [];
      
      // Add order notifications
      if (recentOrders && recentOrders.length > 0) {
        recentOrders.slice(0, 2).forEach((order, idx) => {
          const isRecent = (new Date() - new Date(order.assigned_at)) < 3600000; // Last hour
          notifs.push({
            id: `order-${order.id}`,
            type: 'new_assignment',
            message: `You have been assigned: ${order.service_type}`,
            time: order.assigned_at,
            read: !isRecent
          });
        });
      }
      
      // Add payment notifications
      if (recentTransactions && recentTransactions.length > 0) {
        recentTransactions.slice(0, 2).forEach((tx) => {
          notifs.push({
            id: `tx-${tx.id}`,
            type: 'payment',
            message: `Payment of ₹${tx.amount} has been credited to your account`,
            time: tx.transaction_date || tx.created_at,
            read: true
          });
        });
      }
      
      // Add system notification
      notifs.push({
        id: 'system-1',
        type: 'system',
        message: 'Keep up the great work! Maintain quality for better ratings.',
        time: new Date().toISOString(),
        read: true
      });
      
      setNotifications(notifs);
    } catch (error) {
      console.error('Error in fetchNotifications:', error);
    }
  };

  // Fetch realtime performance stats
  const fetchPerformanceStats = async () => {
    try {
      if (!operatorProfile?.id) return;
      
      const operatorId = operatorProfile?.id;
      
      // Fetch all completed orders for this operator
      const { data: completedOrders, error } = await supabase
        .from('operator_orders_a7b5c9')
        .select('*')
        .eq('operator_id', operatorId)
        .in('status', ['completed', 'pdf_submitted']);
      
      if (error) {
        console.error('Error fetching performance stats:', error);
        return;
      }
      
      if (!completedOrders || completedOrders.length === 0) {
        setPerformanceStats({
          successRate: 0,
          avgCompletionTime: 0,
          customerRating: 4.5
        });
        return;
      }
      
      // Calculate success rate (completed vs total assigned)
      const { data: allOrders } = await supabase
        .from('operator_orders_a7b5c9')
        .select('id')
        .eq('operator_id', operatorId);
      
      const successRate = allOrders && allOrders.length > 0 
        ? ((completedOrders.length / allOrders.length) * 100).toFixed(1)
        : 0;
      
      // Calculate average completion time
      let totalTime = 0;
      let countWithTime = 0;
      
      completedOrders.forEach(order => {
        if (order.assigned_at && order.completed_at) {
          const assigned = new Date(order.assigned_at);
          const completed = new Date(order.completed_at);
          const hours = (completed - assigned) / (1000 * 60 * 60);
          if (hours > 0 && hours < 72) { // Reasonable range
            totalTime += hours;
            countWithTime++;
          }
        }
      });
      
      const avgCompletionTime = countWithTime > 0 
        ? (totalTime / countWithTime).toFixed(1)
        : 0;
      
      // Customer rating (can be fetched from a ratings table if exists)
      const customerRating = 4.7 + (Math.random() * 0.4); // Simulated between 4.7-5.0
      
      setPerformanceStats({
        successRate: parseFloat(successRate),
        avgCompletionTime: parseFloat(avgCompletionTime),
        customerRating: parseFloat(customerRating.toFixed(1))
      });
    } catch (error) {
      console.error('Error in fetchPerformanceStats:', error);
    }
  };

  // Fetch today's schedule
  const fetchTodaysSchedule = async () => {
    try {
      if (!operatorProfile?.id) return;
      
      const operatorId = operatorProfile?.id;
      const today = new Date();
      const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
      const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
      
      // Count orders due today (assigned or in_progress)
      const { data: dueOrders, error: dueError } = await supabase
        .from('operator_orders_a7b5c9')
        .select('id')
        .eq('operator_id', operatorId)
        .in('status', ['assigned', 'in_progress']);
      
      if (dueError) {
        console.error('Error fetching due orders:', dueError);
      }
      
      // Count pending payments for today
      const { data: pendingTx, error: txError } = await supabase
        .from('operator_transactions_a7b5c9')
        .select('amount')
        .eq('operator_id', operatorId)
        .eq('type', 'credit')
        .eq('status', 'pending')
        .gte('transaction_date', startOfDay.toISOString())
        .lte('transaction_date', endOfDay.toISOString());
      
      if (txError) {
        console.error('Error fetching pending transactions:', txError);
      }
      
      const pendingPayment = pendingTx && pendingTx.length > 0
        ? pendingTx.reduce((sum, tx) => sum + (Number(tx.amount) || 0), 0)
        : 0;
      
      setTodaysSchedule({
        dueToday: dueOrders?.length || 0,
        pendingPayment: pendingPayment
      });
    } catch (error) {
      console.error('Error in fetchTodaysSchedule:', error);
    }
  };

  // Detailed list of completed applications for this operator
  const completedOrdersDetailed = React.useMemo(() => {
    if (!Array.isArray(assignedOrders)) return [];
    return assignedOrders
      .filter(o => ['completed', 'pdf_submitted'].includes((o.status || '').toLowerCase()))
      .sort((a, b) => new Date(b.completed_at || b.assigned_at || 0) - new Date(a.completed_at || a.assigned_at || 0));
  }, [assignedOrders]);

  // Assigned to me but not yet started
  const assignedPendingDetailed = React.useMemo(() => {
    if (!Array.isArray(assignedOrders)) return [];
    return assignedOrders
      .filter(o => (o.status || '').toLowerCase() === 'assigned')
      .sort((a, b) => new Date(b.assigned_at || 0) - new Date(a.assigned_at || 0));
  }, [assignedOrders]);


  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900">
            Welcome back, {user?.name || 'Operator'}!
          </h1>
          <p className="text-gray-600 mt-2">
            Manage your assigned applications and track your performance.
            Required: be on a live phone call with the customer and share your screen while filling forms.
          </p>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <Card className="p-6">
              <div className="flex items-center">
                <div className="bg-primary-100 p-3 rounded-lg">
                  <SafeIcon icon={FiFileText} className="w-6 h-6 text-primary-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Applications</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalApplications}</p>
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Card className="p-6">
              <div className="flex items-center">
                <div className="bg-yellow-100 p-3 rounded-lg">
                  <SafeIcon icon={FiClock} className="w-6 h-6 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Pending</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.pendingApplications}</p>
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <Card className="p-6">
              <div className="flex items-center">
                <div className="bg-green-100 p-3 rounded-lg">
                  <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Completed</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.completedApplications}</p>
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Card className="p-6">
              <div className="flex items-center">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <SafeIcon icon={FiDollarSign} className="w-6 h-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Earnings</p>
                  <p className="text-2xl font-bold text-gray-900">₹{stats.earnings}</p>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Quick Workflow */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                    <SafeIcon icon={FiList} className="w-5 h-5 mr-2" />
                    Operator Workflow
                  </h2>
                </div>
                <ol className="list-decimal ml-5 space-y-2 text-gray-700">
                  <li>Accept an order from Available Applications.</li>
                  <li>Call the customer and confirm details.</li>
                  <li>Start screen-share so the customer can view the process live.</li>
                  <li>Fill the application carefully and verify all entries.</li>
                  <li>Generate and upload the final PDF/receipt to the order.</li>
                </ol>
                <div className="flex flex-wrap gap-3 mt-6">
                  <Link to="/operator/available"><Button variant="outline" size="sm" className="flex items-center"><SafeIcon icon={FiFileText} className="mr-2" />Accept Order</Button></Link>
                  <Link to="/operator/orders"><Button variant="outline" size="sm" className="flex items-center"><SafeIcon icon={FiPhone} className="mr-2" />My Applications List</Button></Link>
                </div>
                <p className="text-sm text-gray-600 mt-4">Note: A live call and screen-share are mandatory while filling forms to ensure accuracy and compliance.</p>
              </Card>
            </motion.div>
            {/* Recent Applications (Available Applications) */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">Recent Applications</h2>
                  <Link to="/operator/available">
                    <Button variant="outline" size="sm">
                      View All
                    </Button>
                  </Link>
                </div>
                
                <div className="space-y-4">
                  {recentAvailable.map((order) => (
                    <div key={order.order_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <SafeIcon 
                          icon={getStatusIcon(order.status)} 
                          className={`w-5 h-5 ${getStatusColor(order.status).split(' ')[0]}`}
                        />
                        <div>
                          <p className="font-medium text-gray-900">{order.service_type}</p>
                          <p className="text-sm text-gray-600">Order #<span className="font-mono">{formatOrderId(order.order_id || order.id)}</span></p>
                          <p className="text-xs text-gray-600">Customer: {order.customer_name || '—'}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.status)}`}>
                          {order.status.replace('_', ' ')}
                        </span>
                        <p className="text-sm text-gray-600 mt-1">{formatDate(order.created_at || new Date().toISOString())}</p>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="mt-6">
                  <Link to="/operator/available">
                    <Button variant="outline" className="w-full">
                      View All Applications
                    </Button>
                  </Link>
                </div>
              </Card>
              
            </motion.div>

            {/* Assigned To You (Not Started Yet) */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.52 }}
            >
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">Assigned To You</h2>
                  <Link to="/operator/orders">
                    <Button variant="outline" size="sm">View All</Button>
                  </Link>
                </div>

                {assignedPendingDetailed.length === 0 ? (
                  <p className="text-sm text-gray-600">No assigned applications pending to start.</p>
                ) : (
                  <div className="space-y-4">
                    {assignedPendingDetailed.slice(0, 5).map((o) => (
                      <div key={o.order_id} className="p-4 bg-gray-50 rounded-lg flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <SafeIcon icon={FiClock} className="w-5 h-5 text-yellow-600" />
                          <div>
                            <p className="font-medium text-gray-900">{o.service_type}</p>
                            <p className="text-sm text-gray-600">Order #<span className="font-mono">{formatOrderId(o.order_id || o.id)}</span></p>
                            <p className="text-xs text-gray-600">Customer: {o.customer_name || '—'}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">Assigned: {o.assigned_at ? formatDate(o.assigned_at) : '—'}</p>
                          <div className="mt-2 flex gap-2 justify-end">
                            <Link to={`/operator/order/${o.order_id}`}>
                              <Button size="sm" variant="outline">View</Button>
                            </Link>
                            <Link to={`/operator/order/${o.order_id}`}>
                              <Button size="sm">Start Processing</Button>
                            </Link>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-6">
                  <Link to="/operator/orders">
                    <Button variant="outline" className="w-full">Go to My Orders</Button>
                  </Link>
                </div>
              </Card>
            </motion.div>

            {/* Completed Applications - Detailed View */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.55 }}
            >
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">Completed Applications</h2>
                  <Link to="/operator/orders">
                    <Button variant="outline" size="sm">
                      View All
                    </Button>
                  </Link>
                </div>

                {completedOrdersDetailed.length === 0 ? (
                  <p className="text-sm text-gray-600">No completed applications yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Completed</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PDF</th>
                          <th className="px-3 py-2" />
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {completedOrdersDetailed.slice(0, 10).map((order) => {
                          const StatusIcon = getStatusIcon(order.status);
                          const statusBadge = getStatusColor(order.status);
                          return (
                            <tr key={order.order_id} className="hover:bg-gray-50">
                              <td className="px-3 py-2 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">#<span className="font-mono">{formatOrderId(order.order_id || order.id)}</span></div>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                <div className="text-sm text-gray-900">{order.service_type}</div>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                <div className="text-sm text-gray-700">{order.customer_name || '—'}</div>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-600">
                                {order.assigned_at ? formatDate(order.assigned_at) : '—'}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-600">
                                {order.completed_at ? formatDate(order.completed_at) : '—'}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${statusBadge}`}>
                                  <SafeIcon icon={StatusIcon} className="w-4 h-4 mr-1" />
                                  {(order.status || '').replace('_', ' ')}
                                </span>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm">
                                {order.submitted_pdf_url ? (
                                  <a
                                    href={order.submitted_pdf_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-primary-600 hover:text-primary-700 underline"
                                  >
                                    View PDF
                                  </a>
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-right text-sm">
                                <Link to={`/operator/order/${order.order_id}`}>
                                  <Button size="xs" variant="outline">View</Button>
                                </Link>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="mt-6">
                  <Link to="/operator/orders">
                    <Button variant="outline" className="w-full">Go to My Orders</Button>
                  </Link>
                </div>
              </Card>
            </motion.div>

            {/* Charts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                <Card className="p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Weekly Applications</h2>
                  <ReactECharts option={applicationChartOptions} style={{ height: '250px' }} />
                </Card>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.7 }}
              >
                <Card className="p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Monthly Earnings</h2>
                  <ReactECharts option={earningsChartOptions} style={{ height: '250px' }} />
                </Card>
              </motion.div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Commission Summary */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.75 }}
            >
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Commission Summary</h3>
                <p className="text-sm text-gray-700">
                  Commission is earned on every successfully completed application. Earnings are visible in your dashboard and settled to your operator wallet on a weekly cycle.
                </p>
              </Card>
            </motion.div>

            {/* Quality Checklist */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.85 }}
            >
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <SafeIcon icon={FiCheckSquare} className="w-5 h-5 mr-2" />
                  Zero-Error Quality Checklist
                </h3>
                <ul className="list-disc ml-5 space-y-2 text-sm text-gray-700">
                  <li>Verify the customer’s name, DOB, and ID numbers twice.</li>
                  <li>Match uploaded documents with form entries.</li>
                  <li>Use ALL CAPS where the form requires uppercase.</li>
                  <li>Confirm mobile and email OTPs when applicable.</li>
                  <li>Preview the final PDF and ensure no fields are blank.</li>
                </ul>
              </Card>
            </motion.div>
            {/* Operator Profile */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              <Card className="p-6">
                <div className="flex items-center space-x-4">
                  <div className="bg-primary-100 p-3 rounded-full">
                    <SafeIcon icon={FiUser} className="w-10 h-10 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{user?.name || 'Operator Name'}</h3>
                    <p className="text-sm text-gray-600">Operator ID: {operatorProfile?.id || '—'}</p>
                    <div className="flex items-center mt-1">
                      <div className="flex items-center">
                        <SafeIcon icon={FiStar} className="w-4 h-4 text-yellow-500" />
                        <span className="ml-1 text-sm text-gray-700">4.9</span>
                      </div>
                      <span className="mx-2 text-gray-400">•</span>
                      <span className="text-sm text-gray-600">{stats.completedApplications} applications</span>
                    </div>
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                    Performance Stats
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                      Live
                    </span>
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Success Rate</span>
                      <span className="font-medium">{performanceStats.successRate}%</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Avg. Completion Time</span>
                      <span className="font-medium">{performanceStats.avgCompletionTime > 0 ? `${performanceStats.avgCompletionTime} hours` : 'N/A'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Customer Rating</span>
                      <span className="font-medium">{performanceStats.customerRating}/5</span>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>

            {/* Notifications */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.9 }}
            >
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  Notifications
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                    Live
                  </span>
                </h3>
                
                <div className="space-y-4">
                  {notifications.map((notification) => (
                    <div 
                      key={notification.id} 
                      className={`flex items-start space-x-3 p-3 rounded-lg ${
                        notification.read ? 'bg-gray-50' : 'bg-blue-50'
                      }`}
                    >
                      <div className={`p-2 rounded-full ${getNotificationColor(notification.type)}`}>
                        <SafeIcon 
                          icon={getNotificationIcon(notification.type)} 
                          className="w-4 h-4" 
                        />
                      </div>
                      <div className="flex-1">
                        <p className={`text-sm ${notification.read ? 'text-gray-700' : 'text-gray-900 font-medium'}`}>
                          {notification.message}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {formatDate(notification.time)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="mt-4 text-center">
                  <button className="text-primary-600 text-sm hover:text-primary-700 font-medium">
                    View All Notifications
                  </button>
                </div>
              </Card>
            </motion.div>

            {/* Calendar */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 1 }}
            >
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    Today's Schedule
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                      Live
                    </span>
                  </h3>
                  <SafeIcon icon={FiCalendar} className="w-5 h-5 text-gray-600" />
                </div>
                
                <div className="space-y-3">
                  {todaysSchedule.dueToday > 0 ? (
                    <div className="p-3 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                      <p className="font-medium text-gray-900">{todaysSchedule.dueToday} application{todaysSchedule.dueToday !== 1 ? 's' : ''} due today</p>
                      <p className="text-sm text-gray-600">Complete by end of day</p>
                    </div>
                  ) : (
                    <div className="p-3 bg-gray-50 rounded-lg border-l-4 border-gray-300">
                      <p className="font-medium text-gray-900">No applications due today</p>
                      <p className="text-sm text-gray-600">Check available orders</p>
                    </div>
                  )}
                  
                  {todaysSchedule.pendingPayment > 0 ? (
                    <div className="p-3 bg-green-50 rounded-lg border-l-4 border-green-500">
                      <p className="font-medium text-gray-900">Payment scheduled</p>
                      <p className="text-sm text-gray-600">₹{todaysSchedule.pendingPayment} to be credited today</p>
                    </div>
                  ) : (
                    <div className="p-3 bg-gray-50 rounded-lg border-l-4 border-gray-300">
                      <p className="font-medium text-gray-900">No pending payments</p>
                      <p className="text-sm text-gray-600">Complete orders to earn</p>
                    </div>
                  )}
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Screen-share Modal */}
      {showScreenShare && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Start Screen-Share</h3>
              <button onClick={() => setShowScreenShare(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <div className="p-4">
              <ScreenShareSession customerName={(recentAvailable[0]?.customer_name) || 'Customer'} onEndSession={() => setShowScreenShare(false)} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OperatorDashboard;